// @ts-nocheck
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
    ArrowRight,
    FileText,
    ShieldCheck,
    BookOpen,
    Sparkles,
    Scale,
    CheckCircle2
} from "lucide-react";

const colors = {
    bgTop: "#0b1d33",
    bgBottom: "#081424",
    primary: "#d6b16f",
    primaryHover: "#c29c58",
    accent: "#1d7b78",
    cardBg: "rgba(15, 39, 68, 0.55)",
    cardBorder: "rgba(214, 177, 111, 0.25)",
    text: "#f5f7fa",
    textMuted: "#b6c2cf",
};

export default function ThemisPage() {
    useEffect(() => {
        document.title = "Themis Cloud AI — Νομική Σύνταξη & Έρευνα | Orion Intelligence";
        return () => {
            document.title = "Orion Intelligence";
        };
    }, []);

    const features = [
        { icon: FileText, text: "Σύνταξη Εξουσιοδοτήσεων, Εκθέσεων Έρευνας & Δικογράφων από τις πραγματικές πηγές" },
        { icon: BookOpen, text: "Cloud Βιβλιοθήκη Υποδειγμάτων DOCX με αυτόματη διατήρηση γραμματοσειράς, διαστίχου & Justified στοίχισης" },
        { icon: Sparkles, text: "Ταχύτατο AI Vision OCR για σαρωμένες δικαστικές αποφάσεις & έγγραφα υποθέσεων" },
        { icon: CheckCircle2, text: "Πλήρης ιχνηλασιμότητα (Provenance): κάθε στοιχείο παραπέμπει σε ακριβές αρχείο και σελίδα" },
        { icon: ShieldCheck, text: "Ευρωπαϊκή Βάση PostgreSQL (Frankfurt EU) με Row-Level Security (RLS) ανά δικηγόρο" }
    ];

    return (
        <div
            className="min-h-screen flex flex-col relative overflow-hidden"
            style={{
                color: colors.text,
                background: `radial-gradient(ellipse at 20% 0%, rgba(29, 123, 120, 0.22), transparent 50%),
                             radial-gradient(ellipse at 80% 100%, rgba(214, 177, 111, 0.12), transparent 40%),
                             linear-gradient(170deg, ${colors.bgTop}, ${colors.bgBottom})`,
            }}
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-grow relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 mb-12"
                >
                    <img
                        src="/assets/orion-logo-800.png"
                        alt="Orion Intelligence logo"
                        className="w-10 h-10 rounded-xl"
                        style={{ boxShadow: "0 0 20px rgba(214, 177, 111, 0.35)" }}
                    />
                    <div className="text-left leading-tight">
                        <div className="text-lg font-bold tracking-wide" style={{ color: colors.primary }}>
                            Orion Intelligence
                        </div>
                        <div className="text-xs" style={{ color: colors.textMuted }}>Products • Themis Cloud AI</div>
                    </div>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-16 items-start">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.15 }}
                    >
                        <div
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6"
                            style={{
                                borderColor: colors.primary,
                                background: "rgba(214, 177, 111, 0.1)",
                                color: colors.primary
                            }}
                        >
                            <Scale size={16} />
                            <span className="text-xs font-bold uppercase tracking-widest">Legal AI Drafting SaaS</span>
                        </div>

                        <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
                            Themis Cloud AI
                        </h1>

                        <p className="text-lg md:text-xl leading-relaxed font-medium mb-8" style={{ color: colors.textMuted }}>
                            Η εξειδικευμένη πλατφόρμα AI Νομικής Σύνταξης με βάση τις πηγές του φακέλου σας.
                            <span className="block mt-2" style={{ color: colors.text }}>
                                Διαβάζει δικαστικές αποφάσεις, δικόγραφα και υποδείγματα `.docx` και παράγει έτοιμα έγγραφα Word με μηδενικές επινοήσεις.
                            </span>
                        </p>

                        <div
                            className="mb-10 p-6 rounded-2xl border"
                            style={{
                                background: "linear-gradient(135deg, rgba(15, 39, 68, 0.7), rgba(214, 177, 111, 0.08))",
                                borderColor: colors.cardBorder
                            }}
                        >
                            <h3 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ color: colors.primary }}>
                                <ShieldCheck size={20} />
                                Αρχιτεκτονική Cloud 50.000 Χρηστών (EU GDPR)
                            </h3>
                            <p className="text-sm md:text-base" style={{ color: colors.textMuted }}>
                                Κάθε δικηγορικό γραφείο διαθέτει απομονωμένο χώρο εργασίας με{" "}
                                <strong style={{ color: colors.text }}>Row-Level Security (RLS)</strong>, προσωπική Cloud Βιβλιοθήκη Υποδειγμάτων και πλήρες ιστορικό υποθέσεων.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div
                            className="p-8 rounded-3xl border backdrop-blur-xl relative overflow-hidden shadow-2xl"
                            style={{
                                background: "rgba(8, 20, 36, 0.75)",
                                borderColor: colors.cardBorder
                            }}
                        >
                            <Scale
                                size={120}
                                className="absolute -right-4 -top-4 opacity-[0.05] pointer-events-none"
                                style={{ color: colors.primary }}
                            />

                            <h2 className="text-2xl font-bold mb-2 relative z-10" style={{ color: colors.text }}>
                                Είσοδος στην Πλατφόρμα
                            </h2>
                            <p className="text-sm mb-8 relative z-10" style={{ color: colors.textMuted }}>
                                Συνδεθείτε ή δημιουργήστε τον λογαριασμό του δικηγορικού σας γραφείου στο Themis Cloud.
                            </p>

                            <div className="space-y-4 relative z-10">
                                <a
                                    href="https://themis.orionintelligence.net"
                                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold transition-all shadow-lg"
                                    style={{
                                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryHover})`,
                                        color: "#0b1d33",
                                        boxShadow: "0 10px 30px -10px rgba(214, 177, 111, 0.45)"
                                    }}
                                >
                                    Άνοιγμα Themis Cloud Web App <ArrowRight size={18} />
                                </a>

                                <p className="text-center text-sm pt-2" style={{ color: colors.textMuted }}>
                                    Απευθείας πρόσβαση:{" "}
                                    <a
                                        href="https://themis.orionintelligence.net"
                                        className="underline hover:opacity-80 transition-opacity"
                                        style={{ color: colors.primary }}
                                    >
                                        themis.orionintelligence.net
                                    </a>
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>

                <div className="mt-16 pt-12" style={{ borderTop: "1px solid rgba(214, 177, 111, 0.15)" }}>
                    <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: colors.text }}>
                        Δυνατότητες Themis Cloud
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, i) => (
                            <div
                                key={i}
                                className="flex items-start gap-4 p-5 rounded-2xl border transition-colors"
                                style={{
                                    background: colors.cardBg,
                                    borderColor: colors.cardBorder
                                }}
                            >
                                <div className="p-2 rounded-xl shrink-0" style={{ background: "rgba(214, 177, 111, 0.12)" }}>
                                    <feature.icon size={22} style={{ color: colors.primary }} />
                                </div>
                                <span className="text-sm font-medium leading-relaxed" style={{ color: colors.textMuted }}>
                                    {feature.text}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-12 text-center">
                    <a
                        href="/"
                        className="inline-flex items-center gap-2 text-sm transition-colors"
                        style={{ color: colors.textMuted }}
                    >
                        ← Επιστροφή στην αρχική
                    </a>
                </div>
            </div>
        </div>
    );
}
